import React, { useContext, useState } from "react";
import style from "./Login.module.css";
import AccountCircle from "@mui/icons-material/AccountCircle";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  Typography
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { StoreContext } from "../../../App.js";
import { SuittService } from "../../../services";
import { setJWTToken, setUserInfoToLocalStorage } from "../../../utils";


const initFormErrors = {
  emailError: false,
  emailInvalid: false,
  passError: false,
  passTooShort: false
};

const Login = observer(() => {
  const navigate = useNavigate();
  const suittService = new SuittService();
  const [showPassword, setShowPassword] = useState(false);
  const [loginForm, setLoginForm] = useState({
    login: "",
    password: "",
    sending: false
  });
  const { auth } = useContext(StoreContext);
  const [loginFormErrors, setLoginFormErrors] = useState(initFormErrors);
  const [serverError, setServerError] = useState(false);

  if (auth.user && auth.user._id) {
    navigate(`/cabinet/users/${auth.user._id}`);
  }

  const doValidateForm = () => {
    const {
      login,
      password
    } = loginForm;

    const errors = { ...initFormErrors };
    const emailRegex = /^\S+@\S+\.[A-Za-z]{2,}$/;
    if (login === "") {
      errors.emailError = true;
    } else if (!emailRegex.test(login)) {
      errors.emailError = true;
      errors.emailInvalid = true;
    }
    if (password === "") {
      errors.passError = true;
    } else if (password.length < 8) {
      errors.passError = true;
      errors.passTooShort = true;
    }

    // If at least one of errors is true - set false state
    const state = !Object.values(errors).some((error) => error);

    setLoginFormErrors({
      ...loginFormErrors,
      ...errors
    });

    auth.errors = [];

    return state;
  };

  const doLogin = async () => {
    const isValidForm = doValidateForm();

    if (!isValidForm) {
      return;
    }

    setLoginForm({ ...loginForm, sending: true });
    setServerError(false);
    const result = await suittService.doLogin(loginForm);

    if (result.status) {
      setJWTToken(result.data.token);
      setUserInfoToLocalStorage(result.data.user);
      auth.setUser(result.data.user);
      return navigate(`/cabinet/users/${result.data.user._id}`);
    }

    const errorsArr = Array.isArray(result?.errors) ? result.errors : [];
    const isServerErr = errorsArr.some((e) => {
      const code = e?.code ?? e?.errorCode;
      if (typeof code === 'number' && code >= 500000 && code < 600000) return true;
      const msg = (e?.message || e?.errorMessage || '').toString().toLowerCase();
      return msg.includes('internal server error') || msg.includes('server error');
    });
    if (isServerErr) setServerError(true);

    setLoginForm({ ...loginForm, sending: false });
  };

  return (
    <div className={style.loginWrapper}>
      <form className={style.loginForm}>
        <Typography gutterBottom variant="h5" align="center">
          {"Вхід до облікового запису"}
        </Typography>
        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {"Сталася помилка на сервері. Будь ласка, спробуйте пізніше."}
          </Alert>
        )}
        {
          auth.errors.map((error) => {
            const code = error?.code ?? error?.errorCode;
            const severity = code === 404000 ? "info" : "error";
            const text = code === 404000 ? "Користувача з такою поштою не знайдено" : error.message;
            return (
              <Alert severity={severity}>
                {text}
              </Alert>
            );
          })
        }
        <FormControl sx={{ m: 1, width: "100%", maxWidth: 420 }} variant="standard">
          <InputLabel htmlFor="standard-adornment-email">Логін</InputLabel>
          <Input
            id="standard-adornment-email"
            type={"email"}
            value={loginForm.login}
            onChange={(e) => setLoginForm({
              ...loginForm,
              login: e.target.value
            })}
            required={true}
            error={loginFormErrors.emailError}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle email visibility"
                >
                  <AccountCircle />
                </IconButton>
              </InputAdornment>
            }
          />
          {loginFormErrors.emailError && (
            <FormHelperText error>
              {loginForm.login === '' ? 'Вкажіть email' : (loginFormErrors.emailInvalid ? 'Вкажіть коректний email' : '')}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl sx={{ m: 1, width: "100%", maxWidth: 420 }} variant="standard">
          <InputLabel
            htmlFor="standard-adornment-password">{"Пароль"}</InputLabel>
          <Input
            id="standard-adornment-password"
            type={showPassword ? "text" : "password"}
            value={loginForm.password}
            autoComplete={"on"}
            onChange={(e) => setLoginForm({
              ...loginForm,
              password: e.target.value
            })}
            required={true}
            error={loginFormErrors.passError}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword((oldState) => !oldState)}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
          {loginFormErrors.passError && (
            <FormHelperText error>
              {loginForm.password === '' ? 'Пароль є обов\'язковим' : (loginFormErrors.passTooShort ? 'Мінімальна довжина пароля 8 символів' : '')}
            </FormHelperText>
          )}
        </FormControl>
        {/*        <Link to="/new/auth/forgot"
              component={RouterLink}
              underline="hover"
              color={"secondary"}
              fontSize={"small"}>{"auth_login_forget_password"}</Link>*/}
        

        {loginForm.sending ? (
          <CircularProgress color={"secondary"} />
        ) : (
          <LoadingButton
            loading={loginForm.sending}
            size="large"
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ maxWidth: 420, mt: 2 }}
            onClick={(e) => {
              e.preventDefault();
              setTimeout(async () => {
                await doLogin();
              }, 100);
            }}
          >
            {"Увійти"}
          </LoadingButton>
        )}
      </form>
    </div>
  );
});


export default Login;
